/**
 * Safe wrapper for sessionStorage operations that handles environments
 * where sessionStorage may be undefined (SSR, testing, disabled storage)
 */

/**
 * Checks if sessionStorage is available
 */
const isSessionStorageAvailable = (): boolean => {
  try {
    return typeof window !== 'undefined' && window.sessionStorage !== null;
  } catch {
    // In some cases accessing sessionStorage can throw (e.g., in Safari private browsing)
    return false;
  }
};

/**
 * Safe sessionStorage wrapper with fallback handling
 */
export const safeSessionStorage = {
  /**
   * Get an item from sessionStorage
   * Returns null if sessionStorage is unavailable or if the item doesn't exist
   */
  getItem(key: string): string | null {
    if (!isSessionStorageAvailable()) {
      return null;
    }

    try {
      return sessionStorage.getItem(key);
    } catch (error) {
      console.warn(`Error reading from sessionStorage:`, error);
      return null;
    }
  },

  /**
   * Set an item in sessionStorage
   * Silently fails if sessionStorage is unavailable
   */
  setItem(key: string, value: string): void {
    if (!isSessionStorageAvailable()) {
      return;
    }

    try {
      sessionStorage.setItem(key, value);
    } catch (error) {
      console.warn(`Error writing to sessionStorage:`, error);
    }
  },

  /**
   * Remove an item from sessionStorage
   * Silently fails if sessionStorage is unavailable
   */
  removeItem(key: string): void {
    if (!isSessionStorageAvailable()) {
      return;
    }

    try {
      sessionStorage.removeItem(key);
    } catch (error) {
      console.warn(`Error removing from sessionStorage:`, error);
    }
  },

  /**
   * Clear all items from sessionStorage
   * Silently fails if sessionStorage is unavailable
   */
  clear(): void {
    if (!isSessionStorageAvailable()) {
      return;
    }

    try {
      sessionStorage.clear();
    } catch (error) {
      console.warn(`Error clearing sessionStorage:`, error);
    }
  },
};
